import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Container,
  Button,
  Typography,
  CircularProgress,
  Grid,
} from "@mui/material";
import type { Post, Category } from "../../lib/sanity/types";
import { searchBlogPosts } from "../../lib/sanity/data";
import BlogCategorySlider from "./BlogCategorySlider";
import BlogCards from "./BlogCards";
import BlogNoPosts from "./BlogNoPosts";
import BlogSearchField from "./BlogSearchField";

interface BlogPageClientProps {
  initialPosts: Post[];
  initialCategories: Category[];
  loading: boolean;
}

const INITIAL_VISIBLE = 9;

const BlogPageClient: React.FC<BlogPageClientProps> = ({
  initialPosts,
  initialCategories,
  loading,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Post[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const filteredPosts = useMemo(() => {
    if (searchQuery.length >= 3) {
      return searchResults;
    }
    if (selectedCategory === "all") {
      return initialPosts;
    }
    return initialPosts.filter(
      (post) => post.category?.slug === selectedCategory
    );
  }, [searchQuery, searchResults, selectedCategory, initialPosts]);

  const displayedPosts = filteredPosts.slice(0, visibleCount);
  const hasMore = filteredPosts.length > visibleCount;

  const handleCategoryChange = useCallback((categorySlug: string) => {
    setSelectedCategory(categorySlug);
    setSearchQuery("");
    setSearchResults([]);
    setVisibleCount(INITIAL_VISIBLE);
  }, []);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => prev + INITIAL_VISIBLE);
  }, []);

  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      setVisibleCount(INITIAL_VISIBLE);
      setHasSearched(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const data = await searchBlogPosts(searchQuery);
        setSearchResults(data);
        setHasSearched(true);
      } catch (err) {
        console.error("Search error:", err);
        setSearchResults([]);
        setHasSearched(true);
      } finally {
        setIsSearching(false);
        setVisibleCount(INITIAL_VISIBLE);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      if (e.target.value.length >= 3) {
        setSelectedCategory("all");
      }
    },
    []
  );

  const handleSearchSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
  }, []);

  const clearSearch = useCallback(() => {
    setSearchQuery("");
    setSearchResults([]);
    setVisibleCount(INITIAL_VISIBLE);
  }, []);

  return (
    <Box
      sx={{
        pb: { xs: 6, md: 10 },
        pt: {
          xs: 12,
          md: 10,
        },
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography
              variant="h3"
              sx={{
                color: "#1e293b",
                fontWeight: 700,
                mb: 2,
                textAlign: "center",
                fontSize: { xs: "36px", md: "48px" },
                lineHeight: 1.2,
              }}
            >
              MeetMyBot Blog — Insights & Updates
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "#64748b",
                mb: 6,
                textAlign: "center",
                fontSize: { xs: 16, md: 18 },
              }}
            >
              Explore related articles:
            </Typography>

            <BlogSearchField
              handleSearchSubmit={handleSearchSubmit}
              searchQuery={searchQuery}
              handleSearchChange={handleSearchChange}
              isSearching={isSearching}
            />

            {searchQuery.length >= 3 && (
              <Box sx={{ mt: 2, mb: 4 }}>
                <Typography variant="caption" sx={{ color: "#1e293b" }}>
                  {isSearching
                    ? "Searching..."
                    : `Found ${searchResults.length} article${
                        searchResults.length !== 1 ? "s" : ""
                      } for "${searchQuery}"`}
                </Typography>
              </Box>
            )}
          </Grid>
        </Grid>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress/>
          </Box>
        ) : (
          <>
            {searchQuery.length < 3 && (
              <Box sx={{ mb: 4, mt: 6 }}>
                <BlogCategorySlider
                  categories={initialCategories}
                  onCategoryChange={handleCategoryChange}
                />
              </Box>
            )}

            {searchQuery.length >= 3 &&
            hasSearched &&
            displayedPosts.length === 0 ? (
              <BlogNoPosts
                searchQuery={searchQuery}
                clearSearch={clearSearch}
              />
            ) : displayedPosts.length > 0 ? (
              <>
                <BlogCards displayedPosts={displayedPosts} />
                {hasMore && (
                  <Box sx={{ textAlign: "center", mt: 6 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={loadMore}
                    >
                      Load more
                    </Button>
                  </Box>
                )}
              </>
            ) : null}
          </>
        )}
      </Container>
    </Box>
  );
};

export default BlogPageClient;
